// Role-mapping helpers for the Manage Access panel (KANP-1475).
//
// Server (KANP-1730) returns raw FGA tuple state — no baked permission
// booleans. UI derives the Owner / Data Reader role label from grants[]
// here. This is the staleness-mitigation contract Joshua Pheneger flagged
// in the #authz thread 2026-06-10: keeping permission derivation client-
// side means a stale render is at most one React Query refetch away,
// and mutations invalidate the access query key so refetch is automatic.

import { match } from 'ts-pattern';

import {
  AccessPrincipal,
  ContractRole,
  Grant
} from '@/types/dataContracts';
import { User as OrgUser } from '@/types/user';

export type PrincipalRow = {
  principal: string;
  type: AccessPrincipal['type'];
  role: ContractRole | null; // null for constellation + userset rows
  hasPii: boolean;
  isAdmin: boolean; // user belongs to org#user_admin (no PII toggle in UI)
  grants: Grant[];
  user?: OrgUser; // resolved when admin_middleware /users contains this id
  // KANP-1894: constellation-only enrichments forwarded straight from
  // the server. PrincipalRow is the UI's mounted-on-table shape, so
  // these get a first-class home (rather than re-deriving from the
  // raw AccessPrincipal at render time).
  yourAccess?: AccessPrincipal['your_access'];
  connectedBy?: AccessPrincipal['connected_by'];
};

/**
 * Derive UI role from a principal's grants[]. Owner > Data Reader.
 *
 * - user_owner present (direct or via Admin userset) → 'owner'
 * - user_querier OR user_pii_querier present → 'data_reader'
 * - otherwise null (e.g. constellation cascade, future subject types)
 */
export const roleFromGrants = ( grants: Grant[] ): ContractRole | null => {
  if ( grants.some( ( g ) => g.relation === 'user_owner' ) ) return 'owner';
  if ( grants.some( ( g ) => g.relation === 'user_querier' || g.relation === 'user_pii_querier' ) ) {
    return 'data_reader';
  }
  return null;
};

export const hasPiiGrant = ( grants: Grant[] ): boolean => grants.some( ( g ) => g.relation === 'user_pii_querier' );

/**
 * Is this principal Admin-by-org-role? Two cases:
 *
 * 1. The `organization:{org}#user_admin` userset row itself. Server emits
 *    it with `type: 'organization_userset'` — UI may render a generic
 *    "All admins" row, but the more useful UX is to expand it against
 *    the org user list and badge individual admins. That expansion lives
 *    in `expandPrincipals()` below.
 *
 * 2. A user:* principal whose user_owner grant carries the admin userset
 *    in `source` (server marks user_owner tuples written via the
 *    Admin userset that way when admin_middleware expansion is added
 *    in a follow-up PR). For now this branch is unreachable but kept so
 *    the row-rendering logic doesn't need to change later.
 */
export const isAdminPrincipal = ( principal: AccessPrincipal ): boolean => {
  if ( principal.type === 'organization_userset' && isAdminUserset( principal.principal ) ) {
    return true;
  }
  // user_owner via admin userset — server marks source with the userset
  // string when admin_middleware expansion ships in a follow-up PR.
  return principal.grants.some( ( g ) => isAdminUserset( g.source ) );
};

export const adminUsersetFor = ( orgId: string ): string => `organization:${ orgId }#user_admin`;

/** Match any `organization:<org>#user_admin` userset principal string —
 * the UI doesn't need to know its own orgId to identify Admin rows. */
const ADMIN_USERSET_PATTERN = /^organization:[^#]+#user_admin$/;
export const isAdminUserset = ( principal: string ): boolean => ADMIN_USERSET_PATTERN.test( principal );

/**
 * Word-boundary match for `admin` token in a role name. Catches
 * "Admin", "Org Admin", "Organization Admin", case-insensitive.
 * Does NOT match a custom role like "DataAdminViewer" because there's
 * no word boundary inside (substring match was fragile).
 */
const ADMIN_ROLE_PATTERN = /\badmin\b/i;

const roleNameMatches = ( roles: { name: string }[], re: RegExp ): boolean => roles.some( ( r ) => re.test( r.name ) );

const userIdFromPrincipal = ( principal: string ): string | null => {
  if ( !principal.startsWith( 'user:' ) ) return null;
  return principal.slice( 'user:'.length );
};

/**
 * Expand the AccessPrincipal list into PrincipalRow[] ready for rendering.
 *
 * Steps:
 * 1. Drop the `organization:{org}#user_admin` userset row from FGA output
 *    and replace it with synthetic per-admin rows. Each org admin shows
 *    up as a user-typed Owner with isAdmin=true. UI hides the PII toggle
 *    on these rows since Admins always have PII via the org branch.
 * 2. Resolve display info (`user`) on user rows from the org user list
 *    that's already fetched for the user picker — zero extra round trip.
 * 3. Compute role / hasPii / isAdmin per row.
 *
 * NB: orgUsers comes from the admin_middleware `/api/v1/users` endpoint
 * the picker already loads (KANP-1475 spec). When that fetch hasn't
 * completed, the admin-userset row falls back to a generic "All admins"
 * row so the panel doesn't flicker.
 */
export const expandPrincipals = (
  principals: AccessPrincipal[],
  orgUsers: OrgUser[]
): PrincipalRow[] => {
  // Use the same Admin predicate everywhere (panel badging, picker
  // filter, userset-row expansion) so the panel can't disagree with
  // itself about who is an Admin.
  const adminUsers = orgUsers.filter( isAdminUser );
  const adminUserIds = new Set( adminUsers.map( ( u ) => u.id ) );
  const userById = new Map( orgUsers.map( ( u ) => [ u.id, u ] ) );

  // First pass: bucket direct user-grants by user id so we can MERGE them
  // into the admin-userset expansion. Previous version dropped the direct
  // grants of any user who was also an Admin — a user with an explicit
  // `user_pii_querier` tuple beyond the Admin-implicit Owner would lose
  // that grant from the rendered row.
  const directGrantsByUserId = new Map<string, Grant[]>();
  for ( const p of principals ) {
    if ( p.type !== 'user' ) continue;
    const userId = userIdFromPrincipal( p.principal );
    if ( !userId ) continue;
    directGrantsByUserId.set( userId, p.grants );
  }

  const rows: PrincipalRow[] = principals.flatMap( ( p ) => {
    return match( p.type )
      .with( 'organization_userset', () => {
        if ( !isAdminUserset( p.principal ) ) {
          // The server emits exactly one userset shape today
          // (organization:{org}#user_admin). If a different userset row
          // ever arrives (model drift, new design), drop it but log so
          // the regression is debuggable instead of mysteriously empty.
          console.warn(
            '[ManageAccess] unexpected organization_userset, dropping:',
            p.principal
          );
          return [];
        }
        // No org users loaded yet — render a single placeholder row.
        if ( adminUsers.length === 0 ) {
          return [ {
            principal: p.principal,
            type: p.type,
            role: roleFromGrants( p.grants ),
            hasPii: true,
            isAdmin: true,
            grants: p.grants
          } ];
        }
        // Expand userset → one Owner row per admin user. Merge the userset
        // grants with any direct grants for the same user — preserves
        // PII/extra tuples that would otherwise be lost in dedupe.
        return adminUsers.map( ( u ) => {
          const direct = directGrantsByUserId.get( u.id ) ?? [];
          const merged = mergeGrants( p.grants, direct );
          return {
            principal: `user:${ u.id }`,
            type: 'user' as const,
            role: roleFromGrants( merged ),
            hasPii: true, // Admins always have PII via the org branch
            isAdmin: true,
            grants: merged,
            user: u
          };
        } );
      } )
      .with( 'user', () => {
        const userId = userIdFromPrincipal( p.principal );
        // Skip users who are ALSO admins — they were merged into the
        // userset expansion above. Avoids two rows for the same person.
        if ( userId && adminUserIds.has( userId ) ) return [];
        return [ {
          principal: p.principal,
          type: p.type,
          role: roleFromGrants( p.grants ),
          hasPii: hasPiiGrant( p.grants ),
          isAdmin: false,
          grants: p.grants,
          user: userId ? userById.get( userId ) : undefined
        } ];
      } )
      // Constellation / service / api_key / unknown share the same passthrough
      // shape — `role` is null because Owner/Data Reader is a user concept.
      // Enumerated explicitly (not via `.otherwise()`) so a new PrincipalType
      // variant fails the type check at `.exhaustive()` instead of silently
      // falling through. Per admin/CLAUDE.md ts-pattern rule for unions.
      .with( 'constellation', 'service', 'api_key', 'unknown', () => [ {
        principal: p.principal,
        type: p.type,
        role: null,
        hasPii: hasPiiGrant( p.grants ),
        isAdmin: false,
        grants: p.grants,
        // Backend (KANP-1894) populates these only for constellation
        // rows; for service/api_key/unknown they're absent. Forwarding
        // unconditionally keeps the row shape uniform — the renderer
        // (ConstellationsAccessTable) only reads them on its own rows.
        yourAccess: p.your_access ?? undefined,
        connectedBy: p.connected_by ?? undefined
      } ] )
      .exhaustive();
  } );

  return rows;
};

/**
 * Merge two grant lists, deduping by (relation, source). Order from the
 * first list is preserved; new entries from the second are appended.
 */
const mergeGrants = ( a: Grant[], b: Grant[] ): Grant[] => {
  const seen = new Set( a.map( ( g ) => `${ g.relation }|${ g.source }` ) );
  const out = [ ...a ];
  for ( const g of b ) {
    const k = `${ g.relation }|${ g.source }`;
    if ( !seen.has( k ) ) {
      seen.add( k );
      out.push( g );
    }
  }
  return out;
};

/** True if the user is an Admin per the cached org user list.
 *
 * Prefers `user.fga_relation === 'user_admin'` (the FGA source of truth,
 * populated by KANP-1481 / PR #1560). Falls back to a word-boundary
 * Auth0-role match (`\\badmin\\b`) when `fga_relation` is undefined —
 * i.e. before #1560 lands, or when the bulk FGA resolution fails.
 *
 * The fallback is safe specifically for admin detection — UNLIKE the
 * Developer case — because Auth0 in this org names admins explicitly
 * (`admin` / `godmode`, per services/fga/authorization.py ROLE_TO_FGA_RELATION)
 * with no role name that could confuse Admin with Member or Developer.
 * Compare [[isDeveloperUser]], which has no fallback because "no Auth0
 * role" applies to both Developers AND Members.
 *
 * Uses word-boundary match on the fallback so a custom role name like
 * "DataAdminViewer" doesn't false-positive — substring-match did. */
export const isAdminUser = ( user: OrgUser ): boolean => {
  if ( user.blocked ) return false;
  if ( user.fga_relation ) return user.fga_relation === 'user_admin';
  return roleNameMatches( user.roles, ADMIN_ROLE_PATTERN );
};

/** True if the user is GRANTABLE (a Developer per the AuthZ design).
 *
 * The 2026-05-08 AuthZ plan says: "UI/API filters user-grant picker to
 * Devs+Admins; rejects Member targets with 400." Admins are excluded
 * here for a separate reason — they have implicit ownership via the
 * `[organization#user_admin]` userset on `user_owner`, so a direct
 * grant is redundant — leaving Developers as the intended set.
 *
 * Source of truth is `user.fga_relation` (populated by KANP-1481 /
 * yocodellm PR #1560 with a single bulk FGA read in
 * `users/manager.org_members__get`). Wire shape is the raw
 * `FgaRelation` literal (`user_member` / `user_developer` /
 * `user_admin`), not the Auth0 role name.
 *
 * No Auth0-role fallback. Auth0 only names Admin roles in this org —
 * Members and Developers both have an empty `roles` list, so any
 * fallback would conflate them and silently expose Members to the
 * picker. If `fga_relation` is unset (FGA unreachable on the bulk
 * read), the user is excluded — failing closed matches the AuthZ
 * intersection semantics (`org_developer` intersection blocks
 * everything when the relation is unresolved). */
export const isDeveloperUser = ( user: OrgUser ): boolean => !user.blocked && user.fga_relation === 'user_developer';

export const partitionRows = ( rows: PrincipalRow[] ) => {
  const owners: PrincipalRow[] = [];
  const dataReaders: PrincipalRow[] = [];
  const constellations: PrincipalRow[] = [];
  for ( const r of rows ) {
    if ( r.type === 'constellation' ) constellations.push( r );
    else if ( r.role === 'owner' ) owners.push( r );
    else if ( r.role === 'data_reader' ) dataReaders.push( r );
  }
  return { owners, dataReaders, constellations };
};

/**
 * Extract a clean user-facing message from an error thrown by `callJson`.
 *
 * `callJson` rejects with `new Error("Status code: 400; {\"detail\":\"...\"}")`
 * — surfacing that raw envelope in a toast is ugly and leaks "Status code"
 * jargon. Pull `detail` out so users see "Members cannot be granted contract
 * access. Promote to Developer first." (the FastAPI message) instead.
 *
 * Falls back to the raw message or `fallback` if the shape doesn't match.
 */
export const extractApiErrorMessage = ( err: unknown, fallback: string ): string => {
  if ( !( err instanceof Error ) ) return fallback;
  // Match "Status code: <N>; <JSON-envelope>"
  const m = err.message.match( /^Status code: \d+; (.+)$/s );
  if ( m ) {
    try {
      const json = JSON.parse( m[ 1 ] );
      if ( typeof json?.detail === 'string' ) return json.detail;
      if ( typeof json?.detail?.message === 'string' ) return json.detail.message;
      if ( typeof json?.message === 'string' ) return json.message;
    } catch {
      // Envelope wasn't JSON — fall through to raw text after the semicolon.
      return m[ 1 ];
    }
  }
  return err.message || fallback;
};
